const PROGRESS_SELECTOR = '[data-rebuy-component-id="tiered_progress_bar"]';
const STEP_SELECTOR = '.rebuy-cart__progress-step';
const STEP_LABEL_SELECTOR = '.rebuy-cart__progress-step-label';
const METER_SELECTOR = '.rebuy-cart__progress-bar-meter';
const FILL_SELECTOR = '.rebuy-cart__progress-bar-meter-fill';
const PROMPT_SELECTOR = '.rebuy-cart__progress-bar-prompt';
const UNLOCKED_CLASS = 'is-unlocked';

/**
 * Keeps the first tier of the Rebuy tiered progress bar on the full cart value while the
 * products listed in the theme settings stay excluded from the discount tiers.
 *
 * Rebuy applies "Search Products to Exclude" to every tier, so a decorative free shipping tier
 * never counts those products. This reads the cart from `rebuy:cart.change`, uses the full cart
 * value after discounts, as Shopify's free shipping rate does, for the first tier only, and re-applies after Rebuy re-renders the bar. Rebuy's own
 * rendered progress tells it what Rebuy counted, so partly excluded products need no variant list.
 */
class CartFreeShippingBar {
  /** @type {{ threshold: number, nextThreshold: number, productIds: number[], promptTemplate: string } | null} */
  #config = null;
  /** @type {any} */
  #cart = null;
  #frame = 0;
  #requestId = 0;
  /** @type {{ element: Element | null, written: string | null, original: string | null }} */
  #fillState = { element: null, written: null, original: null };
  /** @type {{ element: Element | null, written: string | null, original: string | null }} */
  #promptState = { element: null, written: null, original: null };

  constructor() {
    this.#config = this.#readConfig();
    if (!this.#config) return;

    document.addEventListener('rebuy:cart.change', this.#handleCartChange);
    new MutationObserver(this.#schedule).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.#fetchCart();
  }

  #readConfig() {
    const element = document.querySelector('script[data-free-shipping-config]');
    if (!element?.textContent) return null;

    try {
      const config = JSON.parse(element.textContent);
      return config.productIds?.length ? config : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {Event} event
   */
  #handleCartChange = (event) => {
    const cart = /** @type {CustomEvent} */ (event).detail?.cart?.cart;
    if (!cart?.items) return;

    this.#requestId++;
    this.#cart = cart;
    this.#schedule();
  };

  async #fetchCart() {
    const requestId = ++this.#requestId;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      if (requestId !== this.#requestId) return;

      this.#cart = cart;
      this.#schedule();
    } catch {
      // The Rebuy cart event supplies the cart when this request fails.
    }
  }

  #schedule = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#apply();
    });
  };

  #apply() {
    const progress = document.querySelector(PROGRESS_SELECTOR);
    if (!progress || !this.#cart || !this.#config) return;

    const steps = progress.querySelectorAll(STEP_SELECTOR);
    const fill = /** @type {HTMLElement | null} */ (progress.querySelector(FILL_SELECTOR));
    const prompt = progress.querySelector(PROMPT_SELECTOR);
    if (!steps.length || !fill) return;

    const { threshold, nextThreshold, productIds } = this.#config;
    const tierWidth = 100 / steps.length;
    const rebuy = this.#readRebuyDisplay(fill, prompt);

    const hasExcluded = this.#cart.items.some((item) => productIds.includes(item.product_id));
    const total =
      this.#cart.total_price ??
      this.#cart.items.reduce((sum, item) => sum + (item.final_line_price ?? item.line_price), 0);

    const shippingUnlocked = total >= threshold;
    const rebuyUnlocked = rebuy.percent >= tierWidth;
    const needsCorrection = hasExcluded && !rebuyUnlocked;

    steps[0].classList.toggle(UNLOCKED_CLASS, hasExcluded && shippingUnlocked);

    if (!needsCorrection) {
      this.#restore(fill, prompt);
      this.#syncMeter(progress, fill);
      return;
    }

    const discountTotal = (rebuy.percent / tierWidth) * threshold;
    const percent = shippingUnlocked ? tierWidth : (total / threshold) * tierWidth;
    this.#setFill(progress, fill, percent);

    const nextLabel = steps[1]?.querySelector(STEP_LABEL_SELECTOR)?.textContent?.trim();
    const firstLabel = steps[0].querySelector(STEP_LABEL_SELECTOR)?.textContent?.trim();
    const remaining = shippingUnlocked ? nextThreshold - discountTotal : threshold - total;
    this.#setPrompt(prompt, remaining, shippingUnlocked ? nextLabel : firstLabel);
  }

  /**
   * Reads what Rebuy rendered. Nodes still holding this script's last write are skipped and the
   * remembered Rebuy values are used, since Rebuy does not repaint when its own numbers are unchanged.
   *
   * @param {HTMLElement} fill
   * @param {Element | null} prompt
   * @returns {{ percent: number }}
   */
  #readRebuyDisplay(fill, prompt) {
    const fillState = this.#fillState;
    const fillIsOurs = fillState.element === fill && fill.style.width === fillState.written;

    if (!fillIsOurs) {
      fillState.element = fill;
      fillState.written = null;
      fillState.original = fill.style.width;
    }

    const promptState = this.#promptState;
    const promptIsOurs =
      prompt && promptState.element === prompt && prompt.textContent === promptState.written;

    if (prompt && !promptIsOurs) {
      promptState.element = prompt;
      promptState.written = null;
      promptState.original = prompt.innerHTML;
    }

    return { percent: parseFloat(fillState.original ?? '') || 0 };
  }

  /**
   * @param {HTMLElement} fill
   * @param {Element | null} prompt
   */
  #restore(fill, prompt) {
    const fillState = this.#fillState;
    if (fillState.written !== null && fillState.original !== null) {
      fill.style.width = fillState.original;
      fillState.written = null;
    }

    const promptState = this.#promptState;
    if (prompt && promptState.written !== null && promptState.original !== null) {
      prompt.innerHTML = promptState.original;
      promptState.written = null;
    }
  }

  /**
   * @param {Element} progress
   * @param {HTMLElement} fill
   * @param {number} percent
   */
  #setFill(progress, fill, percent) {
    const value = Math.min(Math.round(percent * 10) / 10, 100);

    fill.style.width = `${value}%`;
    this.#fillState.written = fill.style.width;
    this.#syncMeter(progress, fill);
  }

  /**
   * Rebuy repaints only the fill width, so the attributes derived from it are rewritten here.
   *
   * @param {Element} progress
   * @param {HTMLElement} fill
   */
  #syncMeter(progress, fill) {
    const value = parseFloat(fill.style.width) || 0;
    const rounded = String(Math.round(value));
    const label = fill.querySelector('.sr-only');

    if (fill.getAttribute('aria-valuenow') !== rounded) fill.setAttribute('aria-valuenow', rounded);
    if (label && label.textContent !== `Progress: ${rounded}%`) label.textContent = `Progress: ${rounded}%`;
    progress.querySelector(METER_SELECTOR)?.classList.toggle('no-progress', value <= 0);
  }

  /**
   * @param {Element | null} prompt
   * @param {number} remaining Amount left, in cents.
   * @param {string | undefined} tier
   */
  #setPrompt(prompt, remaining, tier) {
    if (!prompt || !tier || !this.#config) return;

    const amount = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: this.#cart.currency,
    }).format(Math.max(remaining, 0) / 100);

    const [before, afterAmount] = this.#config.promptTemplate.split('__amount__');
    const [between, after] = (afterAmount ?? '').split('__tier__');
    const text = `${before}${amount}${between}${tier}${after ?? ''}`;

    if (prompt.textContent !== text) {
      const strong = document.createElement('b');
      strong.textContent = tier;
      prompt.replaceChildren(
        document.createTextNode(`${before}${amount}${between}`),
        strong,
        document.createTextNode(after ?? '')
      );
    }

    this.#promptState.written = prompt.textContent;
  }
}

new CartFreeShippingBar();
